import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo
} from 'react';

import keycloak from './keycloak';
import {isBearerTokenAuthEnabled} from "./auth";
import {ReactKeycloakProvider, useKeycloak} from "@react-keycloak/web";
import {useQuery} from "@tanstack/react-query";
import {ApiRoutes, mandantClient} from "../../../api/mandant-api";
import {GovosApiRoutes, govosBaseUrl, govosClient} from "../../../api/govos-api";
import {GovOsResult, GovosUserProfile} from "../../../api/govos.types";
import {LayoutSkeleton} from "../../../layout/LayoutSkeleton";
import {getLogoutUrl} from "./mandant";
import {useLocation, useNavigate} from "react-router-dom";
import {RouteKey} from "../../../pages/AppRoutes";

export interface Profile {
  firstName: string;
  lastName: string;
  id: string;
  name: string;
  email: string;
  given_name: string;
  tenant?: string;
  groups: string[];
  roles: string[];
  tenantRoles: string[];
}

export interface AuthContextValue {
  profile?: Profile;
  error?: Error | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  logout: () => void;
  // Only available when bearer token auth is disabled (Keycloak)
  login?: () => void;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export function useProfile() {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error('useProfile must be used within AuthProvider');
  }

  const inGroup = useCallback(
    (group: string) => context.profile?.groups?.includes(group) ?? false,
    [context.profile]
  );

  const hasRole = useCallback(
    (requestedRole: string) => {
      return (
        context.profile?.groups?.find((role) =>
          role.includes(requestedRole)
        ) !== undefined
      );
    },
    [context.profile?.groups]
  );

  const isTenantAdmin = (theProfile: Profile) => {
    return isBearerTokenAuthEnabled()
      ? hasRole('/Administration')
      : theProfile.tenantRoles.includes('/Administration');
  };

  return { ...context, isTenantAdmin, hasRole, inGroup };
}

function ConfiguredKeycloakProvider({ children }: { children: ReactNode }) {
  return (
    <ReactKeycloakProvider
      authClient={keycloak}
      LoadingComponent={<LayoutSkeleton />}
      initOptions={{ checkLoginIframe: false }}
      onTokens={(token) => {
        if (token.token) {
          mandantClient.defaults.headers.common.Authorization = `Bearer ${token.token}`;
        } else {
          delete mandantClient.defaults.headers.common.Authorization;
        }
      }}
    >
      {children}
    </ReactKeycloakProvider>
  );
}

function BearerProfileContent({ children }: { children: ReactNode }) {
  // eslint-disable-next-line @typescript-eslint/naming-convention
  const { keycloak, initialized } = useKeycloak();

  const { data, error, isLoading } = useQuery({
    queryKey: [ApiRoutes.profile()],
    queryFn: () => keycloak.loadUserProfile(),
    enabled: keycloak.authenticated
  });

  const value = useMemo(
    () => ({
      profile: {
        email: keycloak.tokenParsed?.email || '',
        firstName: keycloak.tokenParsed?.given_name || '',
        lastName: keycloak.tokenParsed?.family_name || '',
        id: keycloak.profile?.id || '',
        name: `${keycloak.profile?.firstName ?? '?'} ${keycloak.profile?.lastName ?? '?'}`,
        groups: keycloak.tokenParsed?.groups || [],
        roles: [],
        tenantRoles: [],
        given_name: keycloak.tokenParsed?.given_name || '',
        family_name: keycloak.tokenParsed?.family_name || ''
      } as Profile,
      error,
      login: () => keycloak.login(),
      logout: () => keycloak.logout(),
      isLoading,
      isAuthenticated: Boolean(keycloak.authenticated)
    }),
    [
      data,
      error,
      isLoading,
      keycloak.authenticated,
      keycloak.login,
      keycloak.logout
    ]
  );

  return (
    <AuthContext.Provider value={value}>
      {initialized ? children : <LayoutSkeleton />}
    </AuthContext.Provider>
  );
}

export function BearerProfileProvider({ children }: { children: ReactNode }) {
  return (
    <ConfiguredKeycloakProvider>
      <BearerProfileContent>{children}</BearerProfileContent>
    </ConfiguredKeycloakProvider>
  );
}

interface InternalProfile {
  id: string;
  groups: string[];
  roles: string[];
  email: string;
  name: string;
  given_name: string;
  family_name: string;
}

export function CookieProfileProvider({ children }: { children: ReactNode }) {
  const { data, error, isLoading } = useQuery<InternalProfile | undefined>({
    queryKey: [ApiRoutes.profile()],
    queryFn: () =>
      mandantClient.get<InternalProfile>(ApiRoutes.profile()).then((r) => r.data)
  });

  const logout = useCallback(() => {
    const logoutUrl = getLogoutUrl();

    if (!logoutUrl) {
      return;
    }

    window.location.assign(logoutUrl);
  }, []);

  const value = useMemo(() => {
    const profile: Profile | undefined = data
      ? {
        firstName: data.given_name,
        lastName: data.family_name,
        id: data.id,
        name: data.name,
        tenant: '',
        email: data.email,
        groups: data.groups,
        roles: data.roles,
        tenantRoles: [],
        given_name: data.given_name
      }
      : undefined;

    return {
      profile,
      error,
      isAuthenticated: true,
      isLoading,
      logout
    };
  }, [data, error, logout, isLoading]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

function GovosProfileContent({ children }: { children: ReactNode }) {
  const location = useLocation();
  const navigate = useNavigate();

  const isDashboard = location.pathname === RouteKey.DASHBOARD;
  const isHome = location.pathname === RouteKey.HOME;

  const { data, error, isLoading } = useQuery<GovosUserProfile | undefined>({
    queryKey: [GovosApiRoutes.user()],
    queryFn: () =>
      govosClient
        .get<GovOsResult<GovosUserProfile>>(GovosApiRoutes.user())
        .then((r) => {
          if (r.data.result === 'error') {
            throw new Error(r.data.error.msg);
          }
          return r.data.data;
        }),
    enabled: isDashboard && !!govosBaseUrl
  });

  useEffect(() => {
    if (isDashboard && !data && !isLoading){
      navigate(RouteKey.LOGIN, { replace: true });
    }
    if (isHome) {
      navigate(RouteKey.NewMandant, { replace: true });
    }
  }, [isDashboard, isHome, data, isLoading, navigate]);

  const logout = useCallback(() => {
    govosClient.post(GovosApiRoutes.logout()).then(() => {
      window.location.assign(RouteKey.LOGIN);
    })
  }, []);

  const value = useMemo(() => {
    const profile: Profile | undefined = data
      ? {
        firstName: data.firstname,
        lastName: data.lastname,
        id: data.identifier,
        name: data.dispname,
        tenant: '',
        email: data.email,
        groups: data.groupList.map((group) => group.name),
        roles: [],
        tenantRoles: [],
        given_name: data.firstname
      }
      : undefined;

    return {
      profile,
      error,
      isAuthenticated: true,
      isLoading,
      logout
    };
  }, [data, error, logout, isLoading]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function GovosProfileProvider({ children }: { children: ReactNode }) {
  return (
    <ConfiguredKeycloakProvider>
      <GovosProfileContent>{children}</GovosProfileContent>
    </ConfiguredKeycloakProvider>
  );
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const isMandantConfig = location.pathname === RouteKey.NewMandant;

  if (isBearerTokenAuthEnabled()) {
    if (isMandantConfig) {
      return <BearerProfileProvider>{children}</BearerProfileProvider>;
    }

    return <GovosProfileProvider>{children}</GovosProfileProvider>;
  }

  return <CookieProfileProvider>{children}</CookieProfileProvider>;
}
